// /src/pages/private/admin/academyTabs/courseExam/CourseExamPlayer.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import CourseExamQuestion from "./CourseExamQuestion";
import CourseExamSidebar from "./CourseExamSidebar";
import CourseExamDocumentPanel from "./CourseExamDocumentPanel";
import {
  createEmptyAnswer,
  flattenExamQuestions,
  scoreCourseExam,
} from "../../../../../../utils/courseExamScoring";

export default function CourseExamPlayer({
  exam,
  isLivePreview = false,
  persistCompletions = false,
  memberExamMode = false,
  attemptId = null,
  passPercentThreshold = 70,
  onExamFinished = null,
}) {
  const [phase, setPhase] = useState("exam");
  const [activeView, setActiveView] = useState("qcm");
  const [answersByQuestionId, setAnswersByQuestionId] = useState({});
  const [results, setResults] = useState(null);
  const [submitState, setSubmitState] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(null);
  const submitLockRef = useRef(false);

  const modules = exam?.modules || [];
  const documents = useMemo(() => {
    if (memberExamMode) return [];

    const fromExam = Array.isArray(exam?.documents) ? exam.documents : [];
    if (fromExam.length) return fromExam;

    return modules
      .map((module) => module.document)
      .filter(Boolean)
      .sort((a, b) => String(a.label || "").localeCompare(String(b.label || "")));
  }, [exam?.documents, memberExamMode, modules]);

  const flatQuestions = useMemo(() => flattenExamQuestions(modules), [modules]);

  const selectedDocument = useMemo(
    () => documents.find((document) => document.documentName === activeView) || null,
    [documents, activeView]
  );

  useEffect(() => {
    if (!exam) return;

    const initial = {};
    for (const module of exam.modules || []) {
      for (const question of module.questions || []) {
        initial[question.questionId] = createEmptyAnswer(question);
      }
    }
    setAnswersByQuestionId(initial);
    setPhase("exam");
    setResults(null);
    setSubmitState(null);
    setActiveView("qcm");
    setSecondsRemaining(Math.max(0, Number(exam.totalEstimatedSeconds) || 0));
    submitLockRef.current = false;
  }, [exam, memberExamMode]);

  const totalQuestions = flatQuestions.length;

  const handleSubmit = useCallback(async () => {
    if (submitLockRef.current) return;
    submitLockRef.current = true;

    const scored = scoreCourseExam(modules, answersByQuestionId);
    setResults(scored);
    setPhase("results");
    setSecondsRemaining(null);

    if (isLivePreview || !persistCompletions || !exam?.courseId) return;

    setSubmitState("saving");
    try {
      if (memberExamMode && attemptId) {
        await axios.post(
          "/api/user/academy/submit-course-exam",
          {
            attemptId,
            passPercentThreshold: passPercentThreshold ?? exam.passPercentThreshold ?? 70,
            moduleResults: scored.moduleResults,
            overallPercent: scored.percent,
          },
          { withCredentials: true }
        );
      } else {
        await axios.post(
          "/api/admin/academy/submit-course-exam-completions",
          {
            courseId: exam.courseId,
            handleId: exam.handleId,
            passPercentThreshold: exam.passPercentThreshold ?? 70,
            moduleResults: scored.moduleResults,
          },
          { withCredentials: true }
        );
      }
      setSubmitState("saved");
      onExamFinished?.();
    } catch (err) {
      console.error("Failed to persist course exam completions", err);
      setSubmitState("error");
    }
  }, [
    answersByQuestionId,
    attemptId,
    exam,
    isLivePreview,
    memberExamMode,
    modules,
    onExamFinished,
    passPercentThreshold,
    persistCompletions,
  ]);

  useEffect(() => {
    if (phase !== "exam" || secondsRemaining == null) return undefined;

    const timerRunning = memberExamMode || activeView === "qcm";
    if (!timerRunning) return undefined;

    if (secondsRemaining <= 0) {
      if (memberExamMode) handleSubmit();
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setSecondsRemaining((current) => (current == null ? current : current - 1));
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [activeView, handleSubmit, memberExamMode, phase, secondsRemaining]);

  const formatCountdown = (totalSeconds) => {
    const safe = Math.max(0, Number(totalSeconds) || 0);
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
  };

  const handleRetry = () => {
    const initial = {};
    for (const { question } of flatQuestions) {
      initial[question.questionId] = createEmptyAnswer(question);
    }
    setAnswersByQuestionId(initial);
    setResults(null);
    setSubmitState(null);
    setPhase("exam");
    setActiveView("qcm");
    setSecondsRemaining(Math.max(0, Number(exam?.totalEstimatedSeconds) || 0));
  };

  if (!exam) return null;

  if (phase === "results" && results) {
    return (
      <div className="course-exam-results">
        <div className="course-exam-results__hero">
          <p className="course-exam-results__eyebrow">Exam completed</p>
          <h3 className="course-exam-results__score">
            {results.earnedPoints} / {results.maxPoints}
          </h3>
          <p className="course-exam-results__percent">{results.percent}%</p>
          {isLivePreview && (
            <span className="exam-meta__live-badge">Live composition</span>
          )}
          {submitState === "saving" && (
            <p className="course-exam-results__persist">Saving completions…</p>
          )}
          {submitState === "saved" && (
            <p className="course-exam-results__persist course-exam-results__persist--ok">
              {memberExamMode
                ? "Examination attempt recorded."
                : "QCM completions recorded."}
            </p>
          )}
          {submitState === "error" && (
            <p className="course-exam-results__persist course-exam-results__persist--error">
              Could not save completions.
            </p>
          )}
        </div>

        <div className="course-exam-results__modules">
          {results.moduleResults.map((moduleResult) => (
            <div key={moduleResult.moduleName} className="course-exam-results__module-row">
              <span>{moduleResult.moduleName}</span>
              <span>
                {moduleResult.earnedPoints} / {moduleResult.maxPoints}
              </span>
            </div>
          ))}
        </div>

        {!memberExamMode ? (
          <div className="course-exam-results__actions">
            <button
              type="button"
              className="accounting-btn accounting-btn--ghost"
              onClick={handleRetry}
            >
              Retry preview
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`course-exam-player${memberExamMode ? " course-exam-player--member" : ""}`}>
      <div className="exam-meta">
        <strong>Time remaining:</strong>{" "}
        <span className="exam-meta__timer">{formatCountdown(secondsRemaining)}</span>
        <span className="exam-meta__count">
          {totalQuestions} question{totalQuestions === 1 ? "" : "s"}
        </span>
        {isLivePreview && (
          <span className="exam-meta__live-badge">Live composition</span>
        )}
        {memberExamMode ? (
          <span className="exam-meta__live-badge exam-meta__live-badge--member">
            Documents unavailable during exam
          </span>
        ) : null}
      </div>

      <div className="course-exam-player__layout">
        {!memberExamMode ? (
          <CourseExamSidebar
            documents={documents}
            activeView={activeView}
            onSelect={setActiveView}
          />
        ) : null}

        <div className="course-exam-player__content">
          {!memberExamMode && activeView !== "qcm" ? (
            <CourseExamDocumentPanel document={selectedDocument} />
          ) : (
            <>
              {totalQuestions === 0 && (
                <p className="exam-meta exam-meta--empty">
                  <em>No questions in this composition. Adjust module quotas and try again.</em>
                </p>
              )}

              {modules.map((module) => (
                <div key={module.moduleName} className="exam-module">
                  <h4>{module.moduleName}</h4>

                  {(module.questions || []).map((question, questionIndex) => (
                    <CourseExamQuestion
                      key={question.questionId}
                      question={question}
                      index={questionIndex}
                      answer={answersByQuestionId[question.questionId]}
                      onChange={(nextAnswer) =>
                        setAnswersByQuestionId((current) => ({
                          ...current,
                          [question.questionId]: nextAnswer,
                        }))
                      }
                    />
                  ))}
                </div>
              ))}

              {totalQuestions > 0 && (
                <div className="course-exam-player__footer">
                  <button
                    type="button"
                    className="accounting-btn accounting-btn--primary"
                    onClick={handleSubmit}
                    data-resource="private.navbar.academy"
                    data-action="execute"
                    data-route="/api/user/academy/submit-course-exam"
                    data-description="Academy → Submit examination"
                  >
                    {memberExamMode ? "Submit examination" : "Finish exam and see score"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
